import React from "react";

import AfriMedLandingShell from "@/components/layout/AfriMedLandingShell.jsx";
import Button from "@/components/ui/Button.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import {
  resolveCommunicationPreferences,
  updateCommunicationPreferences,
} from "@/services/communicationPreferencesService.js";

function readToken() {
  if (typeof window === "undefined") return "";
  try {
    return String(new URLSearchParams(window.location.search || "").get("token") || "").trim();
  } catch {
    return "";
  }
}

function PreferenceCard({ label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4 shadow-[var(--elev-1)]">
      <span className="mt-1 grid h-[24px] w-[24px] min-w-[24px] shrink-0 place-items-center">
        <Checkbox
          checked={checked}
          onChange={onChange}
          className="!h-[24px] !w-[24px] !min-w-[24px] !shrink-0 !aspect-square"
        />
      </span>
      <span className="min-w-0">
        <span className="block text-title-medium text-[rgb(var(--clr-on-surface))]">{label}</span>
        <span className="mt-1 block text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
          {description}
        </span>
      </span>
    </label>
  );
}

export default function CommunicationPreferencesPage() {
  const { t } = useLanguage();
  const token = React.useMemo(() => readToken(), []);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [verified, setVerified] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [preferences, setPreferences] = React.useState({
    emailEnabled: true,
    appointmentNotifications: true,
    marketing: false,
  });

  React.useEffect(() => {
    let alive = true;

    async function loadPreferences() {
      if (!token) {
        if (alive) {
          setVerified(false);
          setSuccess("");
          setError(t("communications.preferences.invalidLink", "This communication link is invalid or incomplete."));
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setVerified(false);
        setError("");
        setSuccess("");
        const response = await resolveCommunicationPreferences(token);
        if (!alive) return;
        setEmail(String(response?.email || "").trim());
        setPreferences({
          emailEnabled: response?.preferences?.emailEnabled !== false,
          appointmentNotifications: response?.preferences?.appointmentNotifications !== false,
          marketing: response?.preferences?.marketing === true,
        });
        setVerified(true);
      } catch (nextError) {
        if (!alive) return;
        setVerified(false);
        setSuccess("");
        setError(nextError?.message || t("communications.preferences.loadError", "AfriMed™ could not load these communication preferences."));
      } finally {
        if (alive) setLoading(false);
      }
    }

    void loadPreferences();
    return () => {
      alive = false;
    };
  }, [token, t]);

  async function handleSave() {
    if (!token || saving) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const response = await updateCommunicationPreferences({
        token,
        preferences,
      });
      setPreferences({
        emailEnabled: response?.preferences?.emailEnabled !== false,
        appointmentNotifications: response?.preferences?.appointmentNotifications !== false,
        marketing: response?.preferences?.marketing === true,
      });
      setSuccess(t("communications.preferences.success", "Communication preferences updated."));
    } catch (nextError) {
      setError(nextError?.message || t("communications.preferences.saveError", "AfriMed™ could not save these communication preferences."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AfriMedLandingShell
      title={t("communications.preferences.title", "Communication preferences")}
      subtitle={t("communications.preferences.subtitle", "Choose which non-critical AfriMed™ emails you want to receive.")}
      contentClassName="max-w-[560px]"
    >
      {loading ? (
        <div className="w-full text-center text-body-large text-[rgb(var(--clr-on-surface-variant))]">
          {t("communications.preferences.loading", "Loading preferences...")}
        </div>
      ) : error || !verified ? (
        <div className="rounded-[16px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
          {error || t("communications.preferences.invalidLink", "This communication link is invalid or incomplete.")}
        </div>
      ) : (
        <div className="space-y-4">
          <TextField56
            readOnly
            tone="auth"
            value={`${t("communications.preferences.noticePrefix", "Preferences for")} ${email}. ${t("communications.preferences.noticeSuffix", "Security and sign-in emails remain enabled.")}`}
          />

          <PreferenceCard
            label={t("communications.preferences.emailUpdates.label", "Email updates")}
            description={t("communications.preferences.emailUpdates.description", "Allow AfriMed™ to send non-critical email communication to this address.")}
            checked={preferences.emailEnabled}
            onChange={(nextChecked) =>
              setPreferences((current) => ({
                ...current,
                emailEnabled: nextChecked,
                appointmentNotifications: nextChecked ? current.appointmentNotifications : false,
                marketing: nextChecked ? current.marketing : false,
              }))
            }
          />

          <PreferenceCard
            label={t("communications.preferences.appointments.label", "Appointment notifications")}
            description={t("communications.preferences.appointments.description", "Receive appointment reminders and related care coordination email updates.")}
            checked={preferences.appointmentNotifications}
            onChange={(nextChecked) =>
              setPreferences((current) => ({
                ...current,
                appointmentNotifications: nextChecked,
              }))
            }
          />

          <PreferenceCard
            label={t("communications.preferences.marketing.label", "Marketing")}
            description={t("communications.preferences.marketing.description", "Future-safe setting for optional AfriMed™ product updates and announcements.")}
            checked={preferences.marketing}
            onChange={(nextChecked) =>
              setPreferences((current) => ({
                ...current,
                marketing: nextChecked,
              }))
            }
          />

          {success ? (
            <div className="rounded-[16px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-[rgb(var(--afrimed-primary))]">
              {success}
            </div>
          ) : null}

          <div className="pt-2">
            <Button fullWidth onClick={() => void handleSave()} disabled={saving}>
              {saving
                ? t("communications.preferences.saving", "Saving...")
                : t("communications.preferences.save", "Save preferences")}
            </Button>
          </div>
        </div>
      )}
    </AfriMedLandingShell>
  );
}
